import { normalizeToNestedData } from "@/shared";
import { api } from "./api";

const path = (path: string) => `/library-items/v2${path}`;

export const slidesLibraryApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSavedSlides: builder.query({
      query: ({
        userId,
        folderId,
        search,
        archived,
        isPopular,
      }: {
        userId: string;
        // Optional: omitting it (as the AI agent's org-wide saved-slide
        // fetch does — see resolve-saved-slides.ts) drops folder scoping
        // entirely server-side, rather than querying a literal empty
        // folderId that matches nothing.
        folderId?: string;
        search?: string;
        archived?: boolean;
        isPopular?: boolean;
      }) => ({
        url: path(""),
        method: "GET",
        params: {
          folderId,
          userId,
          search,
          ...(archived ? { archived: "true" } : {}),
          ...(isPopular ? { isPopular: "true" } : {}),
        },
      }),
      providesTags: (result) => [
        { type: "slides-library", id: "LIST" },
        ...(result?.map(({ _id }: { _id: string }) => ({
          type: "slides-library",
          id: _id,
        })) || []),
      ],
    }),
    getSlideContent: builder.query({
      query: ({ id }: { id: string }) => ({
        url: path(`/content/${id}`),
        method: "GET",
      }),
      transformResponse: (response: Record<string, unknown>) => {
        const { slide } = response || {};
        if (!slide) return null;
        return (slide as Record<string, unknown>).id
          ? slide
          : normalizeToNestedData([slide])[0];
      },
    }),
    deleteSlide: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: path(`/${id}`),
        method: "DELETE",
      }),
      invalidatesTags: (result) => [
        { type: "slides-library", id: result?._id },
        { type: "slides-library", id: "LIST" },
      ],
    }),
    archiveSlide: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: path(`/archive/${id}`),
        method: "PUT",
      }),
      invalidatesTags: (result) => [
        { type: "slides-library", id: result?._id },
        { type: "slides-library", id: "LIST" },
      ],
    }),
    restoreSlide: builder.mutation({
      query: ({ id }: { id: string }) => ({
        url: path(`/restore/${id}`),
        method: "PUT",
      }),
      invalidatesTags: (result) => [
        { type: "slides-library", id: result?._id },
        { type: "slides-library", id: "LIST" },
      ],
    }),
    createSlide: builder.mutation({
      query: ({
        slide,
        folderId,
        title,
      }: {
        slide: Record<string, unknown>;
        folderId: string;
        title: string;
      }) => ({
        url: path(""),
        method: "POST",
        body: { slide, folderId, title },
      }),
      invalidatesTags: () => [{ type: "slides-library", id: "LIST" }],
    }),
    updateSlide: builder.mutation({
      query: ({
        id,
        title,
        folderId,
        isPopular,
        isPro,
        whenToUse,
      }: {
        id: string;
        title?: string;
        folderId?: string;
        isPopular?: boolean;
        isPro?: boolean;
        // Curator guidance for the presgen layout chooser. Never rendered to a
        // reader — it lives beside the slide, not inside it.
        whenToUse?: string;
      }) => ({
        url: path(`/${id}`),
        method: "PUT",
        body: { title, folderId, isPopular, isPro, whenToUse },
      }),
      invalidatesTags: (result) => [
        { type: "slides-library", id: result?._id },
        { type: "slides-library", id: "LIST" },
      ],
    }),
    updateMultipleSlides: builder.mutation({
      query: ({ ids, folderId }: { ids: string[]; folderId: string }) => ({
        url: path("/many"),
        method: "PUT",
        body: { ids, folderId },
      }),
      invalidatesTags: () => [{ type: "slides-library", id: "LIST" }],
    }),
    reorderSavedSlides: builder.mutation({
      query: ({ slideIds }: { slideIds: string[] }) => ({
        url: path("/reorder"),
        method: "PUT",
        body: { slideIds },
      }),
      invalidatesTags: () => [{ type: "slides-library", id: "LIST" }],
    }),
    deleteMultipleSlides: builder.mutation({
      query: ({ ids }: { ids: string[] }) => ({
        url: path("/many"),
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: () => [{ type: "slides-library", id: "LIST" }],
    }),
    getAiSlides: builder.query({
      query: ({ type, category }: { type: string; category?: string }) => ({
        url: "/slides",
        method: "GET",
        params: { type, category },
      }),
    }),
    getAiSlideContent: builder.query({
      query: ({ id }: { id: string }) => ({
        url: `/slides/${id}/content`,
        method: "GET",
      }),
    }),
    getAiSlideDesign: builder.query({
      query: ({ id }: { id: string }) => ({
        url: `/slides/${id}/design`,
        method: "GET",
      }),
    }),
    searchAiSlides: builder.query({
      query: ({ search, types }: { search: string; types: string }) => ({
        url: "/slides/search",
        method: "GET",
        params: { search, types },
      }),
    }),
  }),
});
export const {
  useGetSavedSlidesQuery,
  useDeleteSlideMutation,
  useArchiveSlideMutation,
  useRestoreSlideMutation,
  useCreateSlideMutation,
  useGetSlideContentQuery,
  useLazyGetSlideContentQuery,
  useUpdateSlideMutation,
  useUpdateMultipleSlidesMutation,
  useReorderSavedSlidesMutation,
  useDeleteMultipleSlidesMutation,
  useLazyGetSavedSlidesQuery,
  useGetAiSlidesQuery,
  useGetAiSlideContentQuery,
  useLazySearchAiSlidesQuery,
  useLazyGetAiSlideDesignQuery,
} = slidesLibraryApi;
